// Subjects CRUD operations using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getSchoolContext } from './school-context'
import { getAccessToken } from './auth-token'
import type {
  Subject,
  CreateSubjectRequest,
  UpdateSubjectRequest,
  SubjectFilters,
} from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

// Get all subjects with filters
export async function getSubjectsDirect(filters: SubjectFilters = {}): Promise<Subject[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')
    
    // Apply school context filter (unless Super Admin without context)
    const schoolContext = getSchoolContext()
    const schoolId = filters.school_id || schoolContext
    
    if (schoolId) {
      params.append('school_id', `eq.${schoolId}`)
    }
    if (filters.search) {
      params.append('or', `(subject_name.ilike.*${filters.search}*,subject_code.ilike.*${filters.search}*)`)
    }
    if (!filters.includeInactive) {
      params.append('is_active', 'eq.true')
    }
    params.append('order', 'subject_name.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/subjects?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch subjects')
    }

    return await response.json()
  })
}

// Get a single subject by ID
export async function getSubjectByIdDirect(id: string): Promise<Subject> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')
    params.append('id', `eq.${id}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/subjects?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Subject not found')
    }

    return await response.json()
  })
}

// Create a new subject
export async function createSubjectDirect(subject: CreateSubjectRequest): Promise<Subject> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/subjects`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(subject),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create subject')
    }

    const data: any[] = await response.json()
    return data[0]
  })
}

// Update a subject
export async function updateSubjectDirect(id: string, updates: UpdateSubjectRequest): Promise<Subject> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/subjects?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update subject')
    }

    const data: any[] = await response.json()
    if (!data[0]) throw new Error('Subject not found')
    
    return data[0]
  })
}

// Check if a subject has any timetable records (used for soft vs hard delete decision)
export async function hasSubjectTimetableRecords(id: string): Promise<boolean> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const checkResp = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?subject_id=eq.${id}&select=id&limit=1`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )
    return checkResp.ok && ((await checkResp.json()) as any[]).length > 0
  })
}

// Delete a subject: soft-delete if records exist, hard-delete otherwise
export async function deleteSubjectDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Check for existing timetable records
    const checkResp = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?subject_id=eq.${id}&select=id&limit=1`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    const hasTimetableRecords = checkResp.ok && ((await checkResp.json()) as any[]).length > 0

    if (hasTimetableRecords) {
      // Soft-delete: set is_active = false and deactivated_at
      const softResp = await fetch(`${SUPABASE_URL}/rest/v1/subjects?id=eq.${id}`, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: false, deactivated_at: new Date().toISOString() }),
      })
      if (!softResp.ok) {
        const error = await softResp.json()
        throw new Error(error.message || 'Failed to deactivate subject')
      }
    } else {
      // Hard-delete
      const response = await fetch(`${SUPABASE_URL}/rest/v1/subjects?id=eq.${id}`, {
        method: 'DELETE',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to delete subject')
      }
    }
  })
}

// Reactivate a soft-deleted subject
export async function reactivateSubjectDirect(id: string): Promise<Subject> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/subjects?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify({ is_active: true }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to reactivate subject')
    }

    const data: any[] = await response.json()
    if (!data[0]) throw new Error('Subject not found')
    return data[0]
  })
}
